import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import GroupsIcon from '@mui/icons-material/Groups';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import SaveIcon from '@mui/icons-material/Save';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import trainingApi from '../../services/trainingApi';
import { useTraining } from '../../context/TrainingContext';

// ============================================================
// Tab Panel
// ============================================================
const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);

// ============================================================
// Stats Card
// ============================================================
const StatCard = ({ icon, label, value, color = 'primary.main' }) => (
  <Paper sx={{ p: 3, textAlign: 'center', height: '100%' }}>
    <Box sx={{ color, mb: 1 }}>{icon}</Box>
    <Typography variant="h4" sx={{ fontWeight: 700 }}>{value ?? '—'}</Typography>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
  </Paper>
);

// ============================================================
// Overview Tab
// ============================================================
const OverviewTab = ({ orgId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await trainingApi.getOrgStats(orgId);
        const body = res.data || res;
        const data = body.data || body;
        if (!cancelled) setStats(data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Failed to load stats');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [orgId]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  if (!stats) return null;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard icon={<GroupsIcon fontSize="large" />} label="Cohorts" value={stats.cohortCount ?? stats.cohorts ?? 0} color="primary.main" />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard icon={<PeopleIcon fontSize="large" />} label="Students" value={stats.studentCount ?? stats.students ?? 0} color="success.main" />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard icon={<SchoolIcon fontSize="large" />} label="Lecturers" value={stats.lecturerCount ?? stats.lecturers ?? 0} color="info.main" />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard icon={<AssignmentIcon fontSize="large" />} label="Sessions" value={stats.sessionCount ?? stats.sessions ?? 0} color="warning.main" />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard icon={<TrendingUpIcon fontSize="large" />} label="Avg Score" value={stats.averageScore != null ? `${Math.round(stats.averageScore)}%` : '—'} color="secondary.main" />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <StatCard icon={<TrendingUpIcon fontSize="large" />} label="Pass Rate" value={stats.passRate != null ? `${Math.round(stats.passRate)}%` : '—'} color="success.dark" />
      </Grid>
    </Grid>
  );
};

// ============================================================
// Cohorts Tab
// ============================================================
const CohortsTab = ({ orgId }) => {
  const navigate = useNavigate();
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCohort, setEditCohort] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', academicYear: '', startDate: '', endDate: '' });
  const [saving, setSaving] = useState(false);

  const loadCohorts = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await trainingApi.listCohorts({ organisationId: orgId });
      const body = res.data || res;
      const data = body.data || body;
      setCohorts(data.cohorts || data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load cohorts');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { loadCohorts(); }, [loadCohorts]);

  const openCreate = () => {
    setEditCohort(null);
    setForm({ name: '', code: '', academicYear: '', startDate: '', endDate: '' });
    setDialogOpen(true);
  };

  const openEdit = (cohort) => {
    setEditCohort(cohort);
    setForm({
      name: cohort.name || '',
      code: cohort.code || '',
      academicYear: cohort.academicYear || '',
      startDate: cohort.startDate ? cohort.startDate.substring(0, 10) : '',
      endDate: cohort.endDate ? cohort.endDate.substring(0, 10) : '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const payload = {
        ...form,
        organisationId: orgId,
        startDate: form.startDate || undefined,
        endDate: form.endDate || undefined,
      };
      if (editCohort) {
        await trainingApi.updateCohort(editCohort._id, payload);
      } else {
        await trainingApi.createCohort(payload);
      }
      setDialogOpen(false);
      loadCohorts();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save cohort');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (cohort) => {
    if (!window.confirm(`Archive cohort "${cohort.name}"? This will set it to inactive.`)) return;
    try {
      setError(null);
      await trainingApi.updateCohort(cohort._id, { status: 'archived' });
      loadCohorts();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to archive cohort');
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'archived': return 'default';
      case 'draft': return 'warning';
      default: return 'default';
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Cohorts</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Create Cohort</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Academic Year</TableCell>
              <TableCell align="center">Students</TableCell>
              <TableCell align="center">Lecturers</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cohorts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>No cohorts yet. Create one to get started.</Typography>
                </TableCell>
              </TableRow>
            ) : cohorts.map((c) => (
              <TableRow key={c._id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/training/admin/cohorts/${c._id}`)}>
                <TableCell>{c.name}</TableCell>
                <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{c.code || '—'}</Typography></TableCell>
                <TableCell>{c.academicYear || '—'}</TableCell>
                <TableCell align="center">{c.studentCount ?? c.students?.length ?? 0}</TableCell>
                <TableCell align="center">{c.lecturerCount ?? c.lecturers?.length ?? 0}</TableCell>
                <TableCell><Chip size="small" label={c.status || 'active'} color={statusColor(c.status)} /></TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                  </Tooltip>
                  {c.status !== 'archived' && (
                    <Tooltip title="Archive">
                      <IconButton size="small" onClick={() => handleArchive(c)}><ArchiveIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editCohort ? 'Edit Cohort' : 'Create Cohort'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Code"
              value={form.code}
              onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value }))}
              helperText="Short unique code for the cohort (e.g., PSY-2025-A)"
              fullWidth
            />
            <TextField
              label="Academic Year"
              value={form.academicYear}
              onChange={(e) => setForm(prev => ({ ...prev, academicYear: e.target.value }))}
              placeholder="e.g., 2025/2026"
              fullWidth
            />
            <TextField
              label="Start Date"
              type="date"
              value={form.startDate}
              onChange={(e) => setForm(prev => ({ ...prev, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              value={form.endDate}
              onChange={(e) => setForm(prev => ({ ...prev, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.name.trim()}>
            {saving ? <CircularProgress size={20} /> : (editCohort ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ============================================================
// Members Tab
// ============================================================
const MembersTab = ({ orgId }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', role: 'student' });
  const [adding, setAdding] = useState(false);
  const [roleEditId, setRoleEditId] = useState(null);
  const [roleEditValue, setRoleEditValue] = useState('');

  const loadMembers = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await trainingApi.getOrgMembers(orgId);
      const body = res.data || res;
      const data = body.data || body;
      setMembers(data.members || data || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const handleAdd = async () => {
    try {
      setAdding(true);
      setError(null);
      await trainingApi.addOrgMember(orgId, addForm);
      setAddDialogOpen(false);
      setAddForm({ email: '', role: 'student' });
      loadMembers();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  const handleRoleUpdate = async (userId) => {
    try {
      setError(null);
      await trainingApi.updateOrgMember(orgId, userId, { role: roleEditValue });
      setRoleEditId(null);
      loadMembers();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to update role');
    }
  };

  const handleRemove = async (member) => {
    const displayName = member.name || member.email || member.userId;
    if (!window.confirm(`Remove "${displayName}" from this organisation?`)) return;
    try {
      setError(null);
      const userId = member.userId || member._id;
      await trainingApi.removeOrgMember(orgId, userId);
      loadMembers();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to remove member');
    }
  };

  const roleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'lecturer': return 'primary';
      case 'student': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return '—';
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Organisation Members</Typography>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setAddDialogOpen(true)}>Add Member</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Joined</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>No members found.</Typography>
                </TableCell>
              </TableRow>
            ) : members.map((m) => {
              const userId = m.userId || m._id;
              const roles = m.orgRoles || m.roles || (m.role ? [m.role] : []);
              return (
                <TableRow key={userId} hover>
                  <TableCell>{m.name || m.firstName ? `${m.firstName || ''} ${m.lastName || ''}`.trim() : '—'}</TableCell>
                  <TableCell>{m.email || '—'}</TableCell>
                  <TableCell>
                    {roleEditId === userId ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          select
                          size="small"
                          value={roleEditValue}
                          onChange={(e) => setRoleEditValue(e.target.value)}
                          sx={{ minWidth: 120 }}
                        >
                          <MenuItem value="training_admin">Training Admin</MenuItem>
                          <MenuItem value="lecturer">Lecturer</MenuItem>
                          <MenuItem value="student">Student</MenuItem>
                        </TextField>
                        <Button size="small" variant="contained" onClick={() => handleRoleUpdate(userId)}>Save</Button>
                        <Button size="small" onClick={() => setRoleEditId(null)}>Cancel</Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {roles.length > 0 ? roles.map((r) => (
                          <Chip key={r} size="small" label={r} color={roleColor(r)} />
                        )) : (
                          <Chip size="small" label={m.role || 'member'} color={roleColor(m.role)} />
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(m.joinedAt || m.createdAt)}</TableCell>
                  <TableCell align="right">
                    {roleEditId !== userId && (
                      <>
                        <Tooltip title="Change Role">
                          <IconButton size="small" onClick={() => { setRoleEditId(userId); setRoleEditValue(roles[0] || m.role || 'student'); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <IconButton size="small" color="error" onClick={() => handleRemove(m)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Member</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email Address"
              type="email"
              value={addForm.email}
              onChange={(e) => setAddForm(prev => ({ ...prev, email: e.target.value }))}
              required
              fullWidth
              helperText="The user must have an existing account"
            />
            <TextField
              select
              label="Role"
              value={addForm.role}
              onChange={(e) => setAddForm(prev => ({ ...prev, role: e.target.value }))}
              fullWidth
            >
              <MenuItem value="training_admin">Training Admin</MenuItem>
              <MenuItem value="lecturer">Lecturer</MenuItem>
              <MenuItem value="student">Student</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={adding || !addForm.email.trim()}>
            {adding ? <CircularProgress size={20} /> : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ============================================================
// Assignments Tab
// ============================================================
const AssignmentsTab = ({ orgId }) => {
  const [assignments, setAssignments] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    cohortId: '',
    scenarioIds: [],
    mode: 'practice',
    dueDate: '',
    maxAttempts: 3,
  });

  const loadData = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      setError(null);
      const [assignRes, cohortRes, scenarioRes] = await Promise.all([
        trainingApi.listAssignments({ organisationId: orgId }),
        trainingApi.listCohorts({ organisationId: orgId }),
        trainingApi.listScenarios({ organisationId: orgId }),
      ]);

      const aBody = assignRes.data || assignRes;
      setAssignments((aBody.data || aBody).assignments || aBody.data || aBody || []);

      const cBody = cohortRes.data || cohortRes;
      setCohorts((cBody.data || cBody).cohorts || cBody.data || cBody || []);

      const sBody = scenarioRes.data || scenarioRes;
      setScenarios((sBody.data || sBody).scenarios || sBody.data || sBody || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setForm({
      title: '',
      description: '',
      cohortId: '',
      scenarioIds: [],
      mode: 'practice',
      dueDate: '',
      maxAttempts: 3,
    });
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      setError(null);
      const payload = {
        title: form.title,
        description: form.description || undefined,
        cohortId: form.cohortId,
        scenarioIds: form.scenarioIds,
        mode: form.mode,
        dueDate: form.dueDate || undefined,
        maxAttempts: form.maxAttempts || undefined,
        organisationId: orgId,
        status: 'published',
      };
      await trainingApi.createAssignment(payload);
      setDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create assignment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (assignment) => {
    if (!window.confirm(`Delete assignment "${assignment.title}"?`)) return;
    try {
      setError(null);
      await trainingApi.deleteAssignment(assignment._id);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete assignment');
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'draft': return 'warning';
      case 'closed': return 'default';
      case 'archived': return 'default';
      default: return 'info';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return '—';
    }
  };

  const getCohortName = (cohortId) => {
    const c = cohorts.find(co => co._id === cohortId);
    return c ? c.name : cohortId || '—';
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Assignments</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Create Assignment</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Cohort</TableCell>
              <TableCell align="center">Scenarios</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Submissions</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(Array.isArray(assignments) ? assignments : []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary" sx={{ py: 4 }}>No assignments yet.</Typography>
                </TableCell>
              </TableRow>
            ) : (Array.isArray(assignments) ? assignments : []).map((a) => (
              <TableRow key={a._id} hover>
                <TableCell>{a.title}</TableCell>
                <TableCell>{getCohortName(a.cohortId || a.cohort)}</TableCell>
                <TableCell align="center">{a.scenarioIds?.length || a.scenarios?.length || 0}</TableCell>
                <TableCell>{formatDate(a.dueDate)}</TableCell>
                <TableCell>
                  <TextField
                    select size="small" variant="standard"
                    value={a.status || 'draft'}
                    onChange={async (e) => {
                      try {
                        await trainingApi.updateAssignment(a._id, { status: e.target.value });
                        loadData();
                      } catch { /* ignore */ }
                    }}
                    sx={{ minWidth: 110 }}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </TextField>
                </TableCell>
                <TableCell align="center">{a.submissionCount ?? a.submissions?.length ?? 0}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDelete(a)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Assignment</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              select
              label="Cohort"
              value={form.cohortId}
              onChange={(e) => setForm(prev => ({ ...prev, cohortId: e.target.value }))}
              required
              fullWidth
            >
              {cohorts.map((c) => (
                <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
              ))}
            </TextField>
            <FormControl fullWidth>
              <InputLabel>Scenarios</InputLabel>
              <Select
                multiple
                value={form.scenarioIds}
                onChange={(e) => setForm(prev => ({ ...prev, scenarioIds: e.target.value }))}
                input={<OutlinedInput label="Scenarios" />}
                renderValue={(selected) => {
                  return selected.map(id => {
                    const s = scenarios.find(sc => sc._id === id);
                    return s ? s.name || s.title : id;
                  }).join(', ');
                }}
              >
                {scenarios.map((s) => (
                  <MenuItem key={s._id} value={s._id}>
                    <Checkbox checked={form.scenarioIds.includes(s._id)} />
                    <ListItemText primary={s.name || s.title} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              select
              label="Mode"
              value={form.mode}
              onChange={(e) => setForm(prev => ({ ...prev, mode: e.target.value }))}
              fullWidth
            >
              <MenuItem value="practice">Practice</MenuItem>
              <MenuItem value="assessed">Assessed</MenuItem>
              <MenuItem value="exam">Exam</MenuItem>
            </TextField>
            <TextField
              label="Due Date"
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm(prev => ({ ...prev, dueDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Max Attempts"
              type="number"
              value={form.maxAttempts}
              onChange={(e) => setForm(prev => ({ ...prev, maxAttempts: parseInt(e.target.value, 10) || 1 }))}
              inputProps={{ min: 1, max: 100 }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving || !form.title.trim() || !form.cohortId}>
            {saving ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ============================================================
// Settings Tab
// ============================================================
const SettingsTab = ({ orgId }) => {
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState({
    brandingName: '',
    academicYearStart: '',
    features: {
      evaluationsEnabled: true,
      assignmentsEnabled: true,
      audioExportEnabled: false,
      scenarioWizardEnabled: true,
      selfEnrolmentEnabled: false,
    },
  });

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await trainingApi.getOrganisation(orgId);
        const body = res.data || res;
        const data = body.data || body;
        if (!cancelled) {
          setOrg(data);
          setSettings({
            brandingName: data.brandingName || data.name || '',
            academicYearStart: data.academicYearStart || data.settings?.academicYearStart || '',
            features: {
              evaluationsEnabled: data.features?.evaluationsEnabled ?? data.settings?.features?.evaluationsEnabled ?? true,
              assignmentsEnabled: data.features?.assignmentsEnabled ?? data.settings?.features?.assignmentsEnabled ?? true,
              audioExportEnabled: data.features?.audioExportEnabled ?? data.settings?.features?.audioExportEnabled ?? false,
              scenarioWizardEnabled: data.features?.scenarioWizardEnabled ?? data.settings?.features?.scenarioWizardEnabled ?? true,
              selfEnrolmentEnabled: data.features?.selfEnrolmentEnabled ?? data.settings?.features?.selfEnrolmentEnabled ?? false,
            },
          });
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Failed to load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [orgId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      await trainingApi.updateOrganisation(orgId, {
        brandingName: settings.brandingName || undefined,
        academicYearStart: settings.academicYearStart || undefined,
        features: settings.features,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = (key) => {
    setSettings(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [key]: !prev.features[key],
      },
    }));
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>Settings saved successfully.</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Organisation Details</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Display Name (Branding)"
            value={settings.brandingName}
            onChange={(e) => setSettings(prev => ({ ...prev, brandingName: e.target.value }))}
            fullWidth
            helperText="Name shown to students and lecturers"
          />
          <TextField
            label="Academic Year Start"
            value={settings.academicYearStart}
            onChange={(e) => setSettings(prev => ({ ...prev, academicYearStart: e.target.value }))}
            placeholder="e.g., September or 09"
            fullWidth
            helperText="Month when the academic year begins"
          />
          {org && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Organisation ID: <code>{org._id}</code>
              </Typography>
              {org.slug && (
                <Typography variant="body2" color="text.secondary">
                  Slug: <code>{org.slug}</code>
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Feature Toggles</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={<Switch checked={settings.features.evaluationsEnabled} onChange={() => toggleFeature('evaluationsEnabled')} />}
            label="Evaluations — Enable AI-powered session evaluations"
          />
          <FormControlLabel
            control={<Switch checked={settings.features.assignmentsEnabled} onChange={() => toggleFeature('assignmentsEnabled')} />}
            label="Assignments — Allow lecturers to create and manage assignments"
          />
          <FormControlLabel
            control={<Switch checked={settings.features.audioExportEnabled} onChange={() => toggleFeature('audioExportEnabled')} />}
            label="Audio Export — Allow exporting sessions as audio files"
          />
          <FormControlLabel
            control={<Switch checked={settings.features.scenarioWizardEnabled} onChange={() => toggleFeature('scenarioWizardEnabled')} />}
            label="Scenario Wizard — AI-assisted scenario creation"
          />
          <FormControlLabel
            control={<Switch checked={settings.features.selfEnrolmentEnabled} onChange={() => toggleFeature('selfEnrolmentEnabled')} />}
            label="Self-Enrolment — Students can join cohorts via invitation codes"
          />
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </>
  );
};

// ============================================================
// Scenarios Tab
// ============================================================
const ScenariosTab = ({ orgId, isAdmin }) => {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [shareTargets, setShareTargets] = useState([]);
  const [cloneTarget, setCloneTarget] = useState('');
  const [cloneIncludePersona, setCloneIncludePersona] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadScenarios = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      setError(null);
      const params = isAdmin ? { organisationId: orgId } : {};
      const res = await trainingApi.listScenarios({ ...params, limit: 200 });
      const body = res.data || res;
      const data = body.data || body;
      setScenarios(data.scenarios || data || []);
    } catch (err) {
      setError('Failed to load scenarios');
    } finally {
      setLoading(false);
    }
  }, [orgId, isAdmin]);

  const loadOrgs = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await trainingApi.listOrganisations();
      const body = res.data || res;
      const data = body.data || body;
      setOrgs(data.organisations || data || []);
    } catch { /* ignore */ }
  }, [isAdmin]);

  useEffect(() => { loadScenarios(); loadOrgs(); }, [loadScenarios, loadOrgs]);

  // Load cohort overrides to show warnings
  const [cohortOverrides, setCohortOverrides] = useState({});
  useEffect(() => {
    if (!orgId) return;
    const loadOverrides = async () => {
      try {
        const res = await trainingApi.listCohorts({ organisationId: orgId });
        const body = res.data || res;
        const data = body.data || body;
        const cohorts = data.cohorts || data || [];
        const overrideMap = {};
        for (const c of cohorts) {
          for (const sa of (c.scenarioAccess || [])) {
            const sid = (sa.scenarioId?._id || sa.scenarioId || '').toString();
            if (!overrideMap[sid]) overrideMap[sid] = [];
            overrideMap[sid].push({ cohortName: c.name, access: sa.access });
          }
        }
        setCohortOverrides(overrideMap);
      } catch { /* ignore */ }
    };
    loadOverrides();
  }, [orgId, scenarios]);

  const handleAccessChange = async (scenarioId, newAccess) => {
    try {
      const overrides = cohortOverrides[scenarioId];
      if (overrides && overrides.length > 0) {
        const cohortNames = overrides.map(o => `${o.cohortName} (${o.access})`).join(', ');
        if (!window.confirm(
          `Note: This scenario has cohort-level overrides in: ${cohortNames}.\n\n` +
          `Cohort overrides take precedence over this org-level setting. ` +
          `To change access for specific cohorts, edit them in the Cohort Detail > Scenarios tab.\n\n` +
          `Continue changing org-level access to "${newAccess}"?`
        )) return;
      }
      await trainingApi.updateScenario(scenarioId, { studentAccess: newAccess });
      loadScenarios();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update access');
    }
  };

  const handleShare = async () => {
    if (!selectedScenario || shareTargets.length === 0) return;
    try {
      setSaving(true);
      await trainingApi.shareScenario(selectedScenario._id, shareTargets);
      setShareDialogOpen(false);
      setShareTargets([]);
      loadScenarios();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to share scenario');
    } finally {
      setSaving(false);
    }
  };

  const handleUnshare = async (scenarioId, targetOrgId) => {
    try {
      await trainingApi.unshareScenario(scenarioId, targetOrgId);
      loadScenarios();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unshare');
    }
  };

  const handleClone = async () => {
    if (!selectedScenario || !cloneTarget) return;
    try {
      setSaving(true);
      await trainingApi.cloneScenario(selectedScenario._id, cloneTarget, cloneIncludePersona);
      setCloneDialogOpen(false);
      setCloneTarget('');
      loadScenarios();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clone scenario');
    } finally {
      setSaving(false);
    }
  };

  const openShare = (scenario) => {
    setSelectedScenario(scenario);
    setShareTargets([]);
    setShareDialogOpen(true);
  };

  const openClone = (scenario) => {
    setSelectedScenario(scenario);
    setCloneTarget('');
    setCloneIncludePersona(true);
    setCloneDialogOpen(true);
  };

  const accessColor = (access) => {
    switch (access) {
      case 'library': return 'success';
      case 'assignment_only': return 'warning';
      default: return 'default';
    }
  };

  const accessLabel = (access) => {
    switch (access) {
      case 'library': return 'Library';
      case 'assignment_only': return 'Assignment Only';
      default: return 'Hidden';
    }
  };

  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Scenarios ({scenarios.length})
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage which scenarios are available to students. Set access to "Library" to make a scenario browsable, "Assignment Only" to restrict it to lecturer assignments, or "Hidden" to keep it private.
      </Typography>

      {scenarios.length === 0 ? (
        <Alert severity="info">No scenarios found for this organisation.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Difficulty</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Org Default Access</TableCell>
                <TableCell>Cohort Availability</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell>Shared</TableCell>
                {isAdmin && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {scenarios.map(s => {
                const overrides = cohortOverrides[s._id];
                const inAnyCohort = overrides && overrides.length > 0;
                return (
                <TableRow key={s._id} hover sx={!inAnyCohort && s.status === 'published' ? { bgcolor: 'warning.50' } : {}}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{s.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.slug}</Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{(s.category || '').replace(/_/g, ' ')}</Typography></TableCell>
                  <TableCell><Chip size="small" label={s.difficulty || 'unknown'} variant="outlined" /></TableCell>
                  <TableCell><Chip size="small" label={s.status || 'draft'} color={s.status === 'published' ? 'success' : 'default'} variant="outlined" /></TableCell>
                  <TableCell>
                    <TextField
                      select size="small" variant="standard"
                      value={s.studentAccess || 'hidden'}
                      onChange={(e) => handleAccessChange(s._id, e.target.value)}
                      sx={{ minWidth: 140 }}
                    >
                      <MenuItem value="library">Library</MenuItem>
                      <MenuItem value="assignment_only">Assignment Only</MenuItem>
                      <MenuItem value="hidden">Hidden</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell>
                    {inAnyCohort ? (
                      <Tooltip title={overrides.map(o => `${o.cohortName}: ${o.access}`).join('\n')}>
                        <Chip size="small"
                          label={`${overrides.length} cohort${overrides.length !== 1 ? 's' : ''}`}
                          color="info" variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Chip size="small" label="Not in any cohort" color="warning" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{s.createdAt ? new Date(s.createdAt).toLocaleDateString() : '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{s.updatedAt ? new Date(s.updatedAt).toLocaleDateString() : '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    {(s.sharedWithOrgs || []).length > 0 ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(s.sharedWithOrgs || []).map(oid => {
                          const org = orgs.find(o => o._id === oid || o._id === oid?.toString());
                          return (
                            <Chip key={oid} size="small" label={org?.name || 'Org'}
                              onDelete={isAdmin ? () => handleUnshare(s._id, oid) : undefined}
                              variant="outlined" color="info" />
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  {isAdmin && (
                    <TableCell align="right">
                      <Tooltip title="Share with Organisation">
                        <IconButton size="small" onClick={() => openShare(s)}><PersonAddIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Clone to Organisation">
                        <IconButton size="small" onClick={() => openClone(s)}><AddIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Share Scenario</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share "{selectedScenario?.name}" with other training organisations. They will be able to see and use this scenario but not edit it.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {orgs.filter(o => o._id !== orgId).map(o => (
              <Box key={o._id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Switch
                  size="small"
                  checked={shareTargets.includes(o._id) || (selectedScenario?.sharedWithOrgs || []).includes(o._id)}
                  onChange={(e) => {
                    if (e.target.checked) setShareTargets(prev => [...prev, o._id]);
                    else setShareTargets(prev => prev.filter(id => id !== o._id));
                  }}
                  disabled={(selectedScenario?.sharedWithOrgs || []).includes(o._id)}
                />
                <Typography variant="body2">{o.name}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleShare} disabled={saving || shareTargets.length === 0}>
            {saving ? 'Sharing...' : 'Share'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Dialog */}
      <Dialog open={cloneDialogOpen} onClose={() => setCloneDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Clone Scenario</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a copy of "{selectedScenario?.name}" in another organisation. The clone will be created as a draft so the receiving org can review and customise it.
          </Typography>
          <TextField
            select fullWidth label="Target Organisation"
            value={cloneTarget} onChange={(e) => setCloneTarget(e.target.value)}
            sx={{ mb: 2 }}
          >
            {orgs.filter(o => o._id !== orgId).map(o => (
              <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={<Switch checked={cloneIncludePersona} onChange={(e) => setCloneIncludePersona(e.target.checked)} />}
            label="Also clone the patient persona"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleClone} disabled={saving || !cloneTarget}>
            {saving ? 'Cloning...' : 'Clone'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ============================================================
// Main Component
// ============================================================
const TrainingAdminDashboard = () => {
  const { user } = useTraining() || {};
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [orgId, setOrgId] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgsError, setOrgsError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadOrgs = async () => {
      try {
        setOrgsLoading(true);
        setOrgsError(null);
        const res = await trainingApi.listOrganisations();
        const body = res.data || res;
        const data = body.data || body;
        const list = data.organisations || data || [];
        if (!cancelled) {
          setOrgs(list);
          if (list.length === 1) {
            setOrgId(list[0]._id);
          }
        }
      } catch (err) {
        if (!cancelled) setOrgsError(err.response?.data?.error || err.message || 'Failed to load organisations');
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    };
    loadOrgs();
    return () => { cancelled = true; };
  }, []);

  if (orgsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (orgsError) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Alert severity="error">{orgsError}</Alert>
      </Box>
    );
  }

  if (orgs.length === 0) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Alert severity="info">You are not associated with any training organisations.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Training Admin</Typography>
        {orgs.length > 1 && (
          <TextField
            select
            label="Organisation"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            sx={{ minWidth: 280 }}
            size="small"
          >
            {orgs.map((o) => (
              <MenuItem key={o._id} value={o._id}>{o.name || o.brandingName || o._id}</MenuItem>
            ))}
          </TextField>
        )}
      </Box>

      {!orgId ? (
        <Alert severity="info">Select an organisation to continue.</Alert>
      ) : (
        <>
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab icon={<TrendingUpIcon />} iconPosition="start" label="Overview" />
              <Tab icon={<GroupsIcon />} iconPosition="start" label="Cohorts" />
              <Tab icon={<PeopleIcon />} iconPosition="start" label="Members" />
              <Tab icon={<AssignmentIcon />} iconPosition="start" label="Assignments" />
              <Tab icon={<LibraryBooksIcon />} iconPosition="start" label="Scenarios" />
              <Tab icon={<SettingsIcon />} iconPosition="start" label="Settings" />
            </Tabs>
          </Paper>

          <TabPanel value={tab} index={0}>
            <OverviewTab orgId={orgId} />
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <CohortsTab orgId={orgId} />
          </TabPanel>
          <TabPanel value={tab} index={2}>
            <MembersTab orgId={orgId} />
          </TabPanel>
          <TabPanel value={tab} index={3}>
            <AssignmentsTab orgId={orgId} />
          </TabPanel>
          <TabPanel value={tab} index={4}>
            <ScenariosTab orgId={orgId} isAdmin={user?.role?.includes('admin')} />
          </TabPanel>
          <TabPanel value={tab} index={5}>
            <SettingsTab orgId={orgId} />
          </TabPanel>
        </>
      )}
    </Box>
  );
};

export default TrainingAdminDashboard;
